import React from "react";
import { pipe } from "fp-ts/function";
import * as A from "fp-ts/Array";
import * as O from "fp-ts/Option";
import * as R from "fp-ts/Record";

import { BarChart } from "src/components/charts/bar_chart";
import { Separator } from "src/components/separator/separator";
import { Body, PageTitle, SectionTitle } from "src/components/typography/typography";
import { FFIBrandedV1Report } from "src/pages/reports/ffi/v1/types";
import { isNotNil } from "src/utils/type_guards";
import { DomainsCircle } from "../../graphics/domains_circle";

import { meta } from "./meta";

/*
 * Types
 */

export type HeartProps = {
    readonly report: FFIBrandedV1Report;
};

/*
 * Main
 */

export const Heart = (props: HeartProps): React.ReactElement => {
    const { report } = props;

    return (
        <div className="main-report-content-wrap">
            <div className="single-domain-result-top-wrap">
                <DomainsCircle activeDomain="heart" inline />
                <PageTitle style={{ color: "#99b84e" }}>Heart</PageTitle>
                <Body>
                    Of all the measures of flourishing faith, the Heart proves the most difficult to measure. External
                    fruit such as spiritual disciplines can be observed and measured. Head knowledge can be assessed.
                    But if “faith is the assurance of things hoped for, the conviction of things not yet seen” (Hebrews
                    11:1), as the evidence of things unseen, we can, however imperfectly, measure the growth of faith in
                    our own lives as we seek to live out our Christian faith more and more faithfully.
                </Body>
                <Body>
                    The FFI identified respondents’ heart disposition towards God, the local church, the Bible, others,
                    and the future. The constructs related to Heart were identified for six of the seven survey groups
                    (students; parents; alumni; teachers; leaders/administrators; and support staff), below.
                </Body>
                <Body>
                    <em>
                        Benchmark data is not included for the purpose of scoring or ranking your school against other
                        schools; rather, it is provided to help your school a) gauge priorities and b) track change over
                        subsequent administrations of the FFI at your school.
                    </em>
                </Body>
                <Separator />
            </div>
            {pipe(
                R.keys(report.value.results.raw.heart),
                A.map((construct) => (
                    <div className="an-opportunity-item domain-result" key={construct}>
                        {pipe(
                            R.lookup(construct, meta),
                            O.map((m) => (
                                <>
                                    <SectionTitle style={{ color: "#99b84e" }}>{m.title}</SectionTitle>
                                    <Body>{m.prologue}</Body>
                                    <BarChart
                                        backgroundColor="#99b94e"
                                        data={[
                                            {
                                                label: report.value.year,
                                                value: report.value.results.raw.heart[construct] * 100,
                                            },
                                            {
                                                label: "FFI Benchmark",
                                                value: report.value.results.benchmarks.heart[construct] * 100,
                                            },
                                        ]}
                                    />
                                    {isNotNil(m.extra) && <Body className="small-domain-text">{m.extra}</Body>}
                                </>
                            )),
                            O.getOrElse(() => <></>),
                        )}
                    </div>
                )),
            )}
        </div>
    );
};
